// MA-720 — the ONE shared treatment for "first number" vs "second number"
// counters in every renderer that draws two operand groups of the same mark
// shape (ten_frame, groups, two_ten_frames_add, two_ten_frames_teen).
//
// THE DEFECT: the two house colours measure ~1.03:1 luminance contrast
// against each other (--pal-orange #E89B6B vs --pal-green #88B591), so to a
// child with red-green colour-vision deficiency — or in greyscale — the two
// addends read as one undifferentiated field of dots (MA-705 RED S-1).
//
// THE FIX (operator decisions D1/O3/O4): keep both fills exactly as rendered
// today and ADD a non-hue channel to operand B only — a 2px dark ink
// outline (--ink #2A1F1A, 14.55:1 against the cream page). Operand A stays a
// solid fill with NO outline. Edge vs no-edge survives greyscale and every
// colour-vision deficiency. Additive: no position, size or count changes.
//
// Every in-scope renderer imports from HERE; none keeps its own orange/green
// literal (pinned by src/components/__tests__/operandSecondChannel.test.mjs,
// which also checks these fallbacks equal the globals.css :root values).
// Fallbacks only matter where the CSS vars are absent (SSR→PNG tooling); the
// live app always resolves the vars from src/styles/globals.css.
//
// Dark mode (MA-720 RED S-5): globals.css `.dark` sets --background to
// var(--ink), which would make B's ink ring the same colour as the page.
// No surface applies `.dark` today; if one ever does, this ring colour must
// be revisited (the pin in operandSecondChannel.test.mjs measures the ring
// against :root --background only).

/** Operand A ("first number") fill — house orange, no outline. */
export const OPERAND_A_FILL = "var(--pal-orange, #E89B6B)";
/** Operand B ("second number") fill — house green. */
export const OPERAND_B_FILL = "var(--pal-green, #88B591)";
/** Operand B's non-hue channel: a dark ink outline. */
export const OPERAND_B_STROKE = "var(--ink, #2A1F1A)";
/** Outline width in viewBox units (half of it falls outside the mark). */
export const OPERAND_B_STROKE_WIDTH = 2;

/** Operand B's non-hue channel ALONE — no fill. MA-726: the hint scaffolds
 *  (ScaffoldF5/T10/CMP/NLMA/CMP3 in components/scaffolds/index.tsx) keep
 *  their own fills (terracotta/gold/--pal-blue) and add exactly this channel
 *  to their SECOND same-shape group, so the second-group treatment is the
 *  one MA-720 chose, defined once, never a per-scaffold literal. */
export const OPERAND_B_OUTLINE = {
  stroke: OPERAND_B_STROKE,
  strokeWidth: OPERAND_B_STROKE_WIDTH,
} as const;

/** SVG presentation props for an operand-A mark (circle or star path). */
export const OPERAND_A_MARK = { fill: OPERAND_A_FILL } as const;

/** SVG presentation props for an operand-B mark (circle or star path). */
export const OPERAND_B_MARK = {
  fill: OPERAND_B_FILL,
  ...OPERAND_B_OUTLINE,
} as const;

/** The SVG presentation props of one operand mark (A's or B's). */
export type OperandMarkProps = {
  readonly fill: string;
  readonly stroke?: string;
  readonly strokeWidth?: number;
  readonly strokeOpacity?: number;
};

/** MA-728 (capture instrument for MA-720) — the value of the hidden
 *  `data-operand-b-outline` attribute on an in-scope renderer's root <svg>,
 *  computed from the mark props the renderer ACTUALLY draws operand B with:
 *  "true" when it drew at least one operand-B mark and that mark's EFFECTIVE
 *  stroke is visible (a stroke colour that is not "none", width > 0, and a
 *  stroke opacity that is absent or > 0 — OPERAND_B_MARK satisfies all
 *  three), "false" when it drew operand-B marks whose outline is missing or
 *  hidden, and undefined (React omits the attribute) when the picture draws
 *  no operand-B mark at all. A closed-set token, never a count. Nothing a
 *  child sees or hears changes. */
export function operandBOutlineAttr(
  markB: OperandMarkProps,
  operandBMarksDrawn: number,
): "true" | "false" | undefined {
  if (!(operandBMarksDrawn > 0)) return undefined;
  const outlined =
    typeof markB.stroke === "string" &&
    markB.stroke !== "" &&
    markB.stroke !== "none" &&
    typeof markB.strokeWidth === "number" &&
    markB.strokeWidth > 0 &&
    (markB.strokeOpacity === undefined || markB.strokeOpacity > 0);
  return outlined ? "true" : "false";
}

/** MA-726 operator ruling R2-rev (RED round 1 SHOULD-2) — when the FIRST
 *  group's mark ALREADY carries a solid outline (ScaffoldNB's part circles:
 *  both drawn with a 1.5 --ink stroke), adding "an outline" to the second
 *  group changes nothing a child can see. Instead the second group gets the
 *  same ink colour at DOUBLE group A's width, solid. NOT a dash: a dash
 *  already means "unknown" elsewhere in the app (the pattern strip's "?"
 *  tile, NLMA's "?" arc). Group A is untouched. Applied by rule (A already
 *  outlined -> B heavy), never by scaffold name. */
export const OPERAND_B_HEAVY_OUTLINE_FACTOR = 2;
export function operandBHeavyOutline(groupAStrokeWidth: number) {
  return { stroke: OPERAND_B_STROKE, strokeWidth: groupAStrokeWidth * OPERAND_B_HEAVY_OUTLINE_FACTOR } as const;
}

/** MA-726 operator ruling R3 — a THIRD same-shape group (ScaffoldCMP3's
 *  right-hand group) cannot be told apart by one binary outline, so it gets
 *  a small cream dot centred in each mark: group 1 solid, group 2 outlined
 *  (OPERAND_B_OUTLINE), group 3 fill + inner dot. Radius in viewBox units,
 *  chosen once. Cream (--cream #FAF3E8) is the page colour, so the dot reads
 *  as a hole in the mark in greyscale and under every colour-vision
 *  deficiency. */
export const THIRD_GROUP_DOT_FILL = "var(--cream, #FAF3E8)";
export const THIRD_GROUP_DOT_RADIUS = 2;
